import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';

/**
 * Service pour gérer les synthèses de recherche dans Firebase
 * Utilise la collection locale: users/{userId}/competences/{competenceId}/syntheses
 */
class SynthesisFirebaseService {
  /**
   * Sauvegarde une synthèse de recherche
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {Object} synthesisData - Données de la synthèse
   * @returns {Promise<Object>} - Résultat avec success et synthesisId
   */
  static async saveSynthesis(userId, competenceId, synthesisData) {
    try {
      const synthesesRef = collection(
        db, 
        'users', 
        userId, 
        'competences', 
        competenceId, 
        'syntheses'
      );
      
      const docRef = await addDoc(synthesesRef, {
        ...synthesisData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Synthèse sauvegardée avec ID:', docRef.id);
      return { success: true, synthesisId: docRef.id };
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la synthèse:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Récupère toutes les synthèses d'un utilisateur pour une compétence
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @returns {Promise<Object>} - Résultat avec success et syntheses
   */
  static async getUserSyntheses(userId, competenceId) {
    try {
      const synthesesRef = collection(
        db, 
        'users', 
        userId, 
        'competences', 
        competenceId, 
        'syntheses'
      );
      
      const q = query(synthesesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const syntheses = [];
      querySnapshot.forEach((doc) => {
        syntheses.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`✅ ${syntheses.length} synthèse(s) récupérée(s)`);
      return { success: true, syntheses };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des synthèses:', error);
      return { success: false, error: error.message, syntheses: [] };
    }
  }

  /**
   * Récupère une synthèse spécifique
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} synthesisId - ID de la synthèse
   * @returns {Promise<Object>} - Résultat avec success et synthesis
   */
  static async getSynthesis(userId, competenceId, synthesisId) {
    try {
      const docRef = doc(
        db, 
        'users', 
        userId, 
        'competences', 
        competenceId, 
        'syntheses', 
        synthesisId
      );
      
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const synthesis = {
          id: docSnap.id,
          ...docSnap.data()
        };
        console.log('✅ Synthèse récupérée:', synthesisId);
        return { success: true, synthesis };
      } else {
        console.warn('⚠️ Synthèse non trouvée:', synthesisId);
        return { success: false, error: 'Synthèse non trouvée' };
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la synthèse:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Met à jour une synthèse
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} synthesisId - ID de la synthèse
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Résultat avec success
   */
  static async updateSynthesis(userId, competenceId, synthesisId, updates) {
    try {
      const docRef = doc(
        db, 
        'users', 
        userId, 
        'competences', 
        competenceId, 
        'syntheses', 
        synthesisId
      );
      
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Synthèse mise à jour:', synthesisId);
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de la synthèse:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Supprime une synthèse
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} synthesisId - ID de la synthèse
   * @returns {Promise<Object>} - Résultat avec success
   */
  static async deleteSynthesis(userId, competenceId, synthesisId) {
    try {
      const docRef = doc(
        db, 
        'users', 
        userId, 
        'competences', 
        competenceId, 
        'syntheses', 
        synthesisId
      );
      
      await deleteDoc(docRef);
      console.log('✅ Synthèse supprimée:', synthesisId);
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur lors de la suppression de la synthèse:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Ajoute une analyse détaillée à une source dans une synthèse
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} synthesisId - ID de la synthèse
   * @param {number} sourceIndex - Index de la source
   * @param {Object} analysis - Analyse détaillée
   * @returns {Promise<Object>} - Résultat avec success
   */
  static async addSourceAnalysis(userId, competenceId, synthesisId, sourceIndex, analysis) {
    try {
      const result = await this.getSynthesis(userId, competenceId, synthesisId);
      
      if (!result.success) {
        return result;
      }
      
      const synthesis = result.synthesis;
      const sources = synthesis.sources || [];
      
      if (sourceIndex >= 0 && sourceIndex < sources.length) {
        sources[sourceIndex].detailedAnalysis = analysis;
        
        const updateResult = await this.updateSynthesis(userId, competenceId, synthesisId, { sources });
        
        if (updateResult.success) {
          console.log('✅ Analyse ajoutée à la source:', sourceIndex);
        }
        
        return updateResult;
      } else {
        console.error('❌ Index de source invalide:', sourceIndex);
        return { success: false, error: 'Index de source invalide' };
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout de l\'analyse:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Recherche des synthèses par terme
   * @param {string} userId - ID de l'utilisateur
   * @param {string} competenceId - ID de la compétence
   * @param {string} searchTerm - Terme de recherche
   * @returns {Promise<Object>} - Résultat avec success et syntheses
   */
  static async searchSyntheses(userId, competenceId, searchTerm) {
    try {
      const result = await this.getUserSyntheses(userId, competenceId);
      
      if (!result.success) {
        return result;
      }
      
      const allSyntheses = result.syntheses;
      
      // Filtrer les synthèses qui contiennent le terme de recherche
      const filtered = allSyntheses.filter(synthesis => {
        const objective = (synthesis.searchObjective || '').toLowerCase();
        const title = (synthesis.title || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        
        return objective.includes(term) || title.includes(term);
      });
      
      console.log(`✅ ${filtered.length} synthèse(s) trouvée(s) pour "${searchTerm}"`);
      return { success: true, syntheses: filtered };
    } catch (error) {
      console.error('❌ Erreur lors de la recherche de synthèses:', error);
      return { success: false, error: error.message, syntheses: [] };
    }
  }
}

export default SynthesisFirebaseService;
